import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PartyPopper, Sparkles, X } from 'lucide-react';
import { ADMIN_LOGIN_PATH } from '../constants/admin';

/** Celebration active until this date (1 month from launch) */
const CELEBRATION_END = new Date('2026-10-11T23:59:59');
const SESSION_KEY = 'kopartner_5m_celebration_seen_v1';

const BALLOON_COLORS = [
  '#a855f7',
  '#ec4899',
  '#fbbf24',
  '#3b82f6',
  '#10b981',
  '#f97316',
  '#8b5cf6',
  '#06b6d4',
];

const CONFETTI_COLORS = ['#fde047', '#f472b6', '#a78bfa', '#34d399', '#60a5fa', '#fb923c'];

function isCelebrationActive() {
  return new Date() <= CELEBRATION_END;
}

function shouldSkipRoute(pathname) {
  return pathname.startsWith('/admin') || pathname === ADMIN_LOGIN_PATH;
}

function buildFloatingBalloons(count = 16) {
  return Array.from({ length: count }, (_, i) => ({
    id: `float-${i}`,
    left: `${4 + Math.random() * 92}%`,
    scale: 0.75 + Math.random() * 0.65,
    delay: Math.random() * 5,
    duration: 9 + Math.random() * 7,
    drift: -40 + Math.random() * 80,
    sway: 2 + Math.random() * 4,
    color: BALLOON_COLORS[i % BALLOON_COLORS.length],
  }));
}

function buildCornerBalloons() {
  return [
    { id: 'c1', side: 'left', top: '8%', color: '#ec4899', scale: 1.1, delay: 0 },
    { id: 'c2', side: 'left', top: '62%', color: '#a855f7', scale: 0.9, delay: 0.4 },
    { id: 'c3', side: 'right', top: '12%', color: '#fbbf24', scale: 1, delay: 0.2 },
    { id: 'c4', side: 'right', top: '58%', color: '#3b82f6', scale: 1.15, delay: 0.6 },
    { id: 'c5', side: 'left', top: '36%', color: '#10b981', scale: 0.85, delay: 0.8 },
    { id: 'c6', side: 'right', top: '38%', color: '#f97316', scale: 0.95, delay: 1 },
  ];
}

function buildConfetti(count = 40) {
  return Array.from({ length: count }, (_, i) => ({
    id: `conf-${i}`,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 4,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 5 + Math.random() * 7,
    rotate: Math.random() * 360,
  }));
}

function Balloon({ color, scale = 1, className = '', style = {} }) {
  return (
    <div
      className={`celebration-balloon ${className}`}
      style={{ ...style, ['--balloon-color']: color, ['--balloon-scale']: scale }}
    >
      <div className="celebration-balloon-body">
        <span className="celebration-balloon-shine" />
      </div>
      <div className="celebration-balloon-knot" />
      <div className="celebration-balloon-string" />
    </div>
  );
}

export default function FiveMillionCelebration() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const floatingBalloons = useMemo(() => buildFloatingBalloons(), []);
  const cornerBalloons = useMemo(() => buildCornerBalloons(), []);
  const confetti = useMemo(() => buildConfetti(), []);

  useEffect(() => {
    if (!isCelebrationActive()) return;
    if (shouldSkipRoute(location.pathname)) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const timer = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(SESSION_KEY, 'true');
  };

  if (!open || !isCelebrationActive()) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="5 Million KoPartners celebration"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={close}
        aria-label="Close celebration"
      />

      {/* Confetti */}
      <div className="celebration-confetti-layer pointer-events-none absolute inset-0 overflow-hidden">
        {confetti.map((piece) => (
          <span
            key={piece.id}
            className="celebration-confetti absolute top-[-5%]"
            style={{
              left: piece.left,
              width: piece.size,
              height: piece.size * 0.55,
              backgroundColor: piece.color,
              animationDelay: `${piece.delay}s`,
              animationDuration: `${piece.duration}s`,
              transform: `rotate(${piece.rotate}deg)`,
            }}
          />
        ))}
      </div>

      {/* Rising balloons from bottom */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {floatingBalloons.map((balloon) => (
          <div
            key={balloon.id}
            className="celebration-balloon-float absolute bottom-[-12%]"
            style={{
              left: balloon.left,
              animationDelay: `${balloon.delay}s`,
              animationDuration: `${balloon.duration}s`,
              ['--balloon-drift']: `${balloon.drift}px`,
              ['--balloon-sway']: `${balloon.sway}s`,
            }}
          >
            <Balloon color={balloon.color} scale={balloon.scale} />
          </div>
        ))}
      </div>

      {/* Side balloons around modal */}
      <div className="pointer-events-none absolute inset-0 hidden sm:block">
        {cornerBalloons.map((balloon) => (
          <div
            key={balloon.id}
            className={`celebration-balloon-side absolute ${balloon.side === 'left' ? 'left-3 md:left-8' : 'right-3 md:right-8'}`}
            style={{
              top: balloon.top,
              animationDelay: `${balloon.delay}s`,
            }}
          >
            <Balloon color={balloon.color} scale={balloon.scale} />
          </div>
        ))}
      </div>

      <div className="relative w-full max-w-lg animate-[fadeIn_0.45s_ease-out]">
        {/* Top balloons on card */}
        <div className="pointer-events-none absolute -top-10 left-4 z-10 hidden sm:block">
          <Balloon color="#ec4899" scale={0.9} className="celebration-balloon-bob" />
        </div>
        <div className="pointer-events-none absolute -top-8 right-8 z-10 hidden sm:block">
          <Balloon color="#fbbf24" scale={0.75} className="celebration-balloon-bob" style={{ animationDelay: '0.5s' }} />
        </div>
        <div className="pointer-events-none absolute -top-6 right-24 z-10 hidden md:block">
          <Balloon color="#a855f7" scale={0.65} className="celebration-balloon-bob" style={{ animationDelay: '1s' }} />
        </div>

        <div className="overflow-hidden rounded-3xl border-2 border-white/30 bg-white shadow-2xl ring-4 ring-purple-200/40">
          <div className="relative bg-gradient-to-br from-purple-700 via-fuchsia-600 to-pink-500 px-6 py-8 text-center text-white">
            <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-30">
              <span className="celebration-sparkle absolute left-[10%] top-[20%] text-2xl">✨</span>
              <span className="celebration-sparkle absolute right-[12%] top-[30%] text-xl" style={{ animationDelay: '0.7s' }}>🎈</span>
              <span className="celebration-sparkle absolute left-[18%] bottom-[18%] text-lg" style={{ animationDelay: '1.2s' }}>🎉</span>
              <span className="celebration-sparkle absolute right-[20%] bottom-[22%] text-2xl" style={{ animationDelay: '0.3s' }}>✨</span>
            </div>

            <button
              type="button"
              onClick={close}
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/30"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
              <PartyPopper className="h-8 w-8" />
            </div>

            <p className="relative mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-white/80">
              🎈 Milestone Celebration 🎈
            </p>
            <h2 className="relative text-3xl font-black leading-tight sm:text-4xl">
              5 Million KoPartners!
            </h2>
            <p className="relative mx-auto mt-3 max-w-md text-base text-white/90 sm:text-lg">
              We&apos;ve reached 5 million KoPartners across India — thank you for growing with us.
            </p>
          </div>

          <div className="space-y-4 px-6 py-6">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-2xl bg-purple-50 px-2 py-3">
                <p className="text-xl font-bold text-purple-700">5M+</p>
                <p className="text-[11px] text-gray-500">KoPartners</p>
              </div>
              <div className="rounded-2xl bg-pink-50 px-2 py-3">
                <p className="text-xl font-bold text-pink-600">500+</p>
                <p className="text-[11px] text-gray-500">Cities</p>
              </div>
              <div className="rounded-2xl bg-amber-50 px-2 py-3">
                <p className="text-xl font-bold text-amber-600">#1</p>
                <p className="text-[11px] text-gray-500">In India</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-3 text-emerald-800">
              <Sparkles className="h-4 w-4 shrink-0" />
              <p className="text-sm font-semibold">Limited-time celebration offers are live!</p>
            </div>

            <button
              type="button"
              onClick={close}
              className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 py-3 text-sm font-bold text-white shadow-lg transition hover:shadow-xl"
            >
              Let&apos;s Celebrate 🎈
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { isCelebrationActive, CELEBRATION_END };
